import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { catchError, map } from 'rxjs/operators';
import { ResetPassword } from './reset-password/reset-password.component';
import { AES, PBKDF2, enc, lib } from 'crypto-js';
import { LocationStrategy } from '@angular/common';
import { Subject } from 'rxjs';
import { EncryptionService } from '../../services/encryption.service';

export interface PreLoginUser {
  userName: string;
  passwordChangeAllowed?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PreLoginService implements CanActivate {
  private _preLoginUser: PreLoginUser;
  private _preLoginUserSubject: Subject<PreLoginUser>;
  private _saltKey: string;
  private _preVerificationSaltKey: string;

  constructor(
    private router: Router,
    private http: HttpClient,
    private location: LocationStrategy,
    private encryptionService: EncryptionService
  ) {
    this._preLoginUserSubject = new Subject();
    this.location.onPopState(() => {
      console.log('%cBack Pressed', 'color: green;font-size: 2em');
      this.preLoginUser = null;
      return false;
    });
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    if (this.preLoginUser?.userName) return true;
    this.router.navigate(['/login'], { replaceUrl: true });
    return false;
  }

  get preLoginUser() {
    return this._preLoginUser;
  }

  set preLoginUser(username: PreLoginUser) {
    this._preLoginUser = username;
    this._preLoginUserSubject.next(username);
  }

  get preLoginUsername() {
    return this.preLoginUser?.userName;
  }

  get preLoginUserObservable() {
    return this._preLoginUserSubject.asObservable();
  }

  get saltKey() {
    return this._saltKey;
  }

  set saltKey(sk: string) {
    this._saltKey = sk;
  }

  get preVerificationSaltKey() {
    return this._preVerificationSaltKey;
  }

  set preVerificationSaltKey(pvs: string) {
    this._preVerificationSaltKey = pvs;
  }

  resetPassword(dataForPost) {
    return this.http
      .put(`/security/forgot-password/reset-password`, dataForPost)
      .pipe(
        map((response: any) => response?.data),
        catchError((error) => {
          throw error;
        })
      );
  }

  verifyUsername(username) {
    return this.http
      .post(`/security/forgot-password/verify-user`, {
        userName: username,
      })
      .pipe(
        map((response: any) => response?.data),
        catchError((error) => {
          throw error;
        })
      );
  }

  asyncPasswordValidations(dataForPost: ResetPassword) {
    return this.http
      .post(`/security/forgot-password/validate-password`, dataForPost)
      .pipe(
        map((response: any) => response?.data),
        catchError((error) => {
          throw error;
        })
      );
  }

  encrypt(plainText: string, salt: string) {
    let key = PBKDF2(
      this.encryptionService?.param2?.toString(),
      enc.Hex.parse(salt),
      {
        keySize: 128 / 32,
        iterations: 1000,
      }
    );

    let encrypted = AES.encrypt(plainText, key, {
      iv: enc.Hex.parse(this.encryptionService.param1),
    });

    return encrypted.ciphertext.toString(enc.Base64);
  }

  encryptWithRandomSalt(plainText: string) {
    let salt = lib.WordArray.random(8)?.toString();
    return this.encrypt(plainText, salt);
  }
}
